/**
 * Unified phone-first login (§2.5) — the single entry point for every role.
 *
 * Mounted twice over: at /patient, /doctor and /pharmacist with the role fixed by the URL,
 * and at /auth for admin/support with the role picked on the Landing page.
 *
 * Flow:
 *   phone        → enter mobile number; backend checks if it's registered.
 *   choose       → known account WITH a password → "enter password" or "login with OTP".
 *   password     → password sign-in.
 *   otp          → enter the code (sent for a new number, or chosen by an existing user).
 *   set-password → brand-new account (no password yet) → create one, then continue.
 *
 * On a completed sign-in the AuthContext is populated and the user is routed per RBAC.
 */

import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import {
  checkPhone,
  discardSession,
  phoneLogin,
  requestOtp,
  resetPassword,
  setInitialPassword,
  unlockAccount,
  verifyOtp,
  type AuthResult,
} from "../../api/auth";
import { ApiError } from "../../api/errors";
import { usePasswordRules } from "../../api/usePasswordRules";
import { roleHome, useAuth } from "../../auth/AuthContext";
import type { Role } from "../../auth/AuthContext";
import { Loading } from "../../auth/RequireRole";
import { rolePresentation } from "../../auth/roleMeta";
import { Logo } from "../../components/Logo";
import { PasswordChecklist } from "../../components/ui/PasswordChecklist";
import { PasswordInput } from "../../components/ui/PasswordInput";
import { PhoneInput } from "../../components/ui/PhoneInput";
import { passesAll } from "../../lib/passwordRules";
import { isValidIndianPhone } from "../../lib/phone";

type Step =
  | "phone"
  | "choose"
  | "acknowledge"
  | "password"
  | "otp"
  | "set-password"
  | "reset-password"
  | "locked";

// What the OTP code-entry screen is for: a normal login, lifting a lockout, or a password reset.
type OtpPurpose = "login" | "unlock" | "forgot";

// Seconds before another code can be asked for: a double click mustn't spend two of the 3 codes
// an hour the API allows a number.
const RESEND_COOLDOWN_S = 30;

export function Login({ role }: { role?: Role }) {
  const { user, bootstrapping, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // The role from the URL (/patient, /doctor, /pharmacist) or, on /auth, the card tapped on
  // the Landing screen. The real post-login destination still comes from the authenticated
  // user's role via roleHome().
  const selectedRole = rolePresentation(
    role ?? (location.state as { role?: string } | null | undefined)?.role,
  );
  // Admin accounts are password-only — no OTP login, forgot-password, unlock or signup: the API
  // never sends an admin a sign-in code. Support signs in like doctors and pharmacists (AUTH-18).
  const isPasswordOnly = selectedRole?.role === "admin";
  const [step, setStep] = useState<Step>("phone");
  const [phone, setPhone] = useState("+91");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [code, setCode] = useState("");
  const [requestId, setRequestId] = useState<string | null>(null);
  const [otpPurpose, setOtpPurpose] = useState<OtpPurpose>("login");
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  // A second click while a code request is in flight sends nothing: two requests sent two codes,
  // made the first INVALID_OTP and spent the 3-an-hour quota (AUTH-21).
  const sendingOtp = useRef(false);
  // The password rule, for the steps that choose a new password (AUTH-15).
  const passwordRules = usePasswordRules({
    enabled: step === "set-password" || step === "reset-password" || otpPurpose === "forgot",
  });

  // console.log(selectedRole)

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const reset = (next: Step) => {
    setStep(next);
  };

  // A sign-in on the wrong card: the number and password (or code) are another role's. This page
  // signs in only its own role, so the session it was given is ended and nothing is stored
  // (AUTH-18) — it used to open that role's portal from, say, the Admin card.
  function refuseOtherRole(result: AuthResult): boolean {
    if (!selectedRole || result.user.role === selectedRole.role) return false;
    if (result.refresh_token) discardSession(result.refresh_token).catch(() => {});
    const theirs = rolePresentation(result.user.role)?.title ?? result.user.role;
    toast.error(
      `This number belongs to ${/^[aeiou]/i.test(theirs) ? "an" : "a"} ${theirs} account. ` +
        `Sign in on the ${theirs} page instead.`,
    );
    return true;
  }

  // Apply a finished AuthResult: store tokens, set user. Returns the user for follow-up routing.
  function applyAuth(result: AuthResult) {
    login(
      {
        id: result.user.id,
        role: result.user.role,
        full_name: result.user.full_name,
      },
      result.access_token && result.refresh_token
        ? { access: result.access_token, refresh: result.refresh_token }
        : undefined,
    );
    return result.user;
  }

  // Step 1 — check the phone, then branch.
  async function onCheckPhone(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedRole) {
      toast.error("Please choose how you want to sign in first.");
      return;
    }
    if (!isValidIndianPhone(phone)) {
      toast.error(
        "Enter a 10-digit mobile number starting with 6, 7, 8, or 9.",
      );
      return;
    }
    if (isPasswordOnly) {
      reset("choose");
      return;
    }
    setBusy(true);
    try {
      // let removePlusPhone = phone.trim();
      // if (removePlusPhone.startsWith("+")) {
      //   removePlusPhone = removePlusPhone.slice(1);
      // }
      
      const { exists, has_password, registered_other_role } = await checkPhone(
        selectedRole.role,
        phone.trim(),
      );
      if (registered_other_role) {
        // The number belongs to a different account type (e.g. an admin). Block signup and
        // keep the user on this screen — don't advance to OTP/acknowledge.
        toast.error("This mobile number is already registered with another user.");
        return;
      }
      if (!exists && selectedRole.role !== "patient") {
        // Staff accounts are created by an administrator; a code here would only create a
        // patient account under the doctor's or pharmacist's number (AUTH-09).
        toast.error(`No ${selectedRole.role} account uses this number. Ask your administrator to add you.`);
        return;
      }
      if (exists && has_password) {
        reset("choose"); // password or OTP
      } else {
        // New number (or an account without a password yet) → confirm before sending the SMS.
        reset("acknowledge");
      }
    } catch (err) {
      toast.error(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  // Send the OTP SMS, then show the code-entry screen. `purpose` decides what verifying the
  // code does: a normal OTP login, or lifting a too-many-failed-attempts lockout.
  async function onSendOtp(purpose: OtpPurpose = "login") {
    if (sendingOtp.current) return;
    sendingOtp.current = true;
    setBusy(true);
    try {
      const { request_id, dev_otp } = await requestOtp(phone.trim(), selectedRole?.role);
      setRequestId(request_id);
      setOtpPurpose(purpose);
      setCode("");
      setResendIn(RESEND_COOLDOWN_S);
      reset("otp");
      // SMS (Twilio) integration is still in progress, so the backend can't text the code yet.
      // Until then it returns the OTP in the response (dev/non-prod only) and we show it here.
      if (dev_otp) {
        toast.info(`Your OTP is ${dev_otp}`, {
          description: "SMS delivery is being integrated — showing the code here for now.",
          duration: 10000,
        });
      }
    } catch (err) {
      toast.error(messageFor(err));
      // The hourly code cap: "Send a new code" waits until it lifts.
      const wait = err instanceof ApiError ? Number(err.details?.retry_after_seconds) : 0;
      if (wait > 0) setResendIn(Math.ceil(wait));
    } finally {
      sendingOtp.current = false;
      setBusy(false);
    }
  }

  async function onPasswordLogin(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const result = await phoneLogin(phone.trim(), password);
      if (refuseOtherRole(result)) return;
      const user = applyAuth(result);
      // A password an admin reset is temporary — change it before going on.
      navigate(result.user.must_change_password ? "/auth/change-password" : roleHome(user.role), {
        replace: true,
      });
    } catch (err) {
      // A too-many-failed-attempts lockout is self-recoverable via OTP — offer that path
      // instead of a dead-end toast. (ACCOUNT_NOT_ACTIVE is admin-managed → plain message.)
      // Admins get no sign-in codes, so another admin unlocks them (AUTH-18).
      if (err instanceof ApiError && err.code === "ACCOUNT_LOCKED" && isPasswordOnly) {
        toast.error("Your account is locked after too many attempts. Ask another administrator to unlock it.");
      } else if (err instanceof ApiError && err.code === "ACCOUNT_LOCKED") {
        reset("locked");
      } else {
        toast.error(messageFor(err));
      }
    } finally {
      setBusy(false);
    }
  }

  async function onVerifyOtp(e: React.FormEvent) {
    e.preventDefault();
    if (!requestId) return;
    setBusy(true);
    try {
      if (otpPurpose === "unlock") {
        // Verify the OTP to lift the lockout; no session is issued — return to password sign-in.
        await unlockAccount(requestId, code.trim());
        toast.success("Account unlocked. Enter your password to sign in.");
        setCode("");
        setPassword("");
        reset("password");
        return;
      }
      if (otpPurpose === "forgot") {
        // Don't consume the OTP yet — carry the code to the reset step, where it's submitted
        // together with the new password in a single call (the OTP is single-use).
        setBusy(false);
        reset("reset-password");
        return;
      }
      const result = await verifyOtp(requestId, code.trim());
      if (refuseOtherRole(result)) {
        setCode("");
        reset("phone");
        return;
      }
      const user = applyAuth(result); // tokens stored → set-password call is authenticated
      if (!result.user.password_set) {
        reset("set-password"); // brand-new account → must create a password
      } else {
        navigate(roleHome(user.role), { replace: true });
      }
    } catch (err) {
      toast.error(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  async function onSetPassword(e: React.FormEvent) {
    e.preventDefault();
    if (!passesAll(passwordRules, newPassword)) {
      toast.error("Your password doesn't meet the rules below yet.");
      return;
    }
    setBusy(true);
    try {
      const user = await setInitialPassword(newPassword);
      navigate(roleHome(user.role), { replace: true });
    } catch (err) {
      toast.error(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  async function onResetPassword(e: React.FormEvent) {
    e.preventDefault();
    if (!requestId) return;
    if (!passesAll(passwordRules, newPassword)) {
      toast.error("Your password doesn't meet the rules below yet.");
      return;
    }
    setBusy(true);
    try {
      await resetPassword(requestId, code.trim(), newPassword);
      toast.success("Password reset. Sign in with your new password.");
      setCode("");
      setPassword("");
      setNewPassword("");
      reset("password");
    } catch (err) {
      toast.error(messageFor(err));
      // The code is only checked here, together with the new password (it is single-use), and
      // this step has no code field: go back to it (AUTH-17). A wrong code is fixed with the same
      // request — no new code, which would count toward the hourly cap; an expired or used-up one
      // needs "Send a new code" there.
      if (err instanceof ApiError && CODE_ERRORS.includes(err.code)) {
        setCode("");
        reset("otp");
      }
    } finally {
      setBusy(false);
    }
  }

  // Wait for the session restore so a signed-in user doesn't see the form flash.
  if (bootstrapping) return <Loading />;
  // Already signed in (e.g. reopening /patient) → straight to their home. Only on the first
  // step: an OTP sign-in for a brand-new account sets the user *before* the set-password
  // step, and the handlers above do their own routing once sign-in completes.
  if (user && step === "phone") return <Navigate to={roleHome(user.role)} replace />;

  return (
    <main className="relative flex min-h-dvh items-center justify-center overflow-hidden bg-sunken px-4 py-10">
      <div className="brand-gradient absolute inset-x-0 top-0 h-1.5" aria-hidden />
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-linear-to-b from-brand-50 via-brand-50/40 to-transparent"
        aria-hidden
      />

      <div className="relative w-full max-w-sm">
        <div className="mb-6 flex justify-center">
          <Logo variant="stacked" size={58} withTagline to="/" />
        </div>

        <div className="card overflow-hidden">
          <div className="brand-gradient h-1" aria-hidden />
          <div className="p-6">
        {selectedRole && (
          <div className="mb-5 flex items-center gap-3 rounded-xl border border-line bg-raised p-3">
            <span
              className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${selectedRole.accent}`}
            >
              {selectedRole.icon}
            </span>
            <span className="flex flex-col">
              <span className="text-xs font-medium uppercase tracking-wide text-ink-muted">
                Signing in as
              </span>
              <span className="text-base font-semibold text-brand-900">
                {selectedRole.title}
              </span>
            </span>
          </div>
        )}
        <h1 className="mb-1 text-xl font-bold text-brand-900">Sign in</h1>
        <p className="mb-6 text-sm text-ink-muted">
          {subtitle(step, phone, otpPurpose)}
        </p>

        {step === "phone" && (
          <form onSubmit={onCheckPhone} className="space-y-4">
            <PhoneInput value={phone} onChange={setPhone} required autoFocus />
            <Submit busy={busy} idle="Continue" busyLabel="Checking…" />
          </form>
        )}

        {step === "choose" && (
          <div className="space-y-3">
            <button
              onClick={() => reset("password")}
              className="btn-primary btn-block"
            >
              Enter password
            </button>
            {!isPasswordOnly && (
              <>
                <div className="flex items-center gap-3 text-xs text-ink-muted">
                  <span className="h-px flex-1 bg-line" />
                  or
                  <span className="h-px flex-1 bg-line" />
                </div>
                <button
                  onClick={() => onSendOtp("login")}
                  disabled={busy}
                  className="btn-secondary btn-block"
                >
                  {busy ? "Sending…" : "Login with OTP"}
                </button>
                <button
                  type="button"
                  onClick={() => onSendOtp("forgot")}
                  disabled={busy}
                  className="link w-full text-center text-sm disabled:opacity-50"
                >
                  Forgot password?
                </button>
              </>
            )}
            <ChangeNumber onClick={() => reset("phone")} />
          </div>
        )}

        {step === "acknowledge" && (
          <div className="space-y-4">
            <p className="text-sm text-ink-soft">
              We’ll send a one-time password (OTP) by SMS to{" "}
              <span className="font-medium">{phone}</span> to verify your
              number. Standard message rates may apply.
            </p>
            <button
              onClick={() => onSendOtp("login")}
              disabled={busy}
              className="btn-primary btn-block"
            >
              {busy ? "Sending…" : "Send OTP"}
            </button>
            <ChangeNumber onClick={() => reset("phone")} />
          </div>
        )}

        {step === "password" && (
          <form onSubmit={onPasswordLogin} className="space-y-4">
            <PasswordInput
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="input"
              required
            />
            <Submit busy={busy} idle="Sign in" busyLabel="Signing in…" />
            {!isPasswordOnly && (
              <>
                <button
                  type="button"
                  onClick={() => onSendOtp("login")}
                  disabled={busy}
                  className="link w-full text-center text-sm disabled:opacity-50"
                >
                  Login with OTP instead
                </button>
                <button
                  type="button"
                  onClick={() => onSendOtp("forgot")}
                  disabled={busy}
                  className="w-full text-center text-sm text-ink-muted hover:text-brand disabled:opacity-50"
                >
                  Forgot password?
                </button>
              </>
            )}
            <ChangeNumber onClick={() => reset("phone")} />
          </form>
        )}

        {step === "otp" && (
          <form onSubmit={onVerifyOtp} className="space-y-4">
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              maxLength={6}
              className="input text-center text-lg tracking-[0.4em]"
              required
            />
            <Submit
              busy={busy}
              idle={
                otpPurpose === "forgot"
                  ? "Verify & set new password"
                  : "Verify & continue"
              }
              busyLabel="Verifying…"
            />
            <button
              type="button"
              onClick={() => onSendOtp(otpPurpose)}
              disabled={busy || resendIn > 0}
              className="w-full text-center text-sm text-brand transition hover:underline disabled:text-ink-muted disabled:no-underline"
            >
              {resendIn > 60
                ? `Send a new code in ${Math.ceil(resendIn / 60)} min`
                : resendIn > 0
                  ? `Send a new code in ${resendIn}s`
                  : "Send a new code"}
            </button>
            <ChangeNumber onClick={() => reset("phone")} />
          </form>
        )}

        {step === "set-password" && (
          <form onSubmit={onSetPassword} className="space-y-4">
            <PasswordInput
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Create a password"
              className="input"
              required
            />
            <PasswordChecklist password={newPassword} rules={passwordRules} />
            <Submit
              busy={busy}
              idle="Set password & continue"
              busyLabel="Saving…"
            />
          </form>
        )}

        {step === "reset-password" && (
          <form onSubmit={onResetPassword} className="space-y-4">
            <PasswordInput
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password"
              className="input"
              required
              autoFocus
            />
            <PasswordChecklist password={newPassword} rules={passwordRules} />
            <Submit busy={busy} idle="Reset password" busyLabel="Resetting…" />
            <ChangeNumber onClick={() => reset("phone")} />
          </form>
        )}

        {step === "locked" && (
          <div className="space-y-4">
            <p className="text-sm text-ink-soft">
              For your security, this account was locked after too many
              incorrect password attempts. Verify your number with a one-time
              password (OTP) to unlock it, then sign in again.
            </p>
            <button
              onClick={() => onSendOtp("unlock")}
              disabled={busy}
              className="btn-primary btn-block"
            >
              {busy ? "Sending…" : "Unlock with OTP"}
            </button>
            <ChangeNumber onClick={() => reset("phone")} />
          </div>
        )}

        {/* Back to role selection — only for roles picked on the Landing page; the
            URL-only roles (patient/doctor/pharmacist) aren't listed there. */}
        {!role && (
        <div className="flex justify-center mt-3">
          <button
            type="button"
            onClick={() => navigate("/")}
            className={`
              inline-flex
              items-center gap-1
              text-sm font-medium
              text-ink-muted transition hover:text-brand
              focus:outline-hidden
            `}
          >
            <span aria-hidden="true">←</span> Back
          </button>
        </div>
        )}
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-ink-muted">
          Express care. Always there.
        </p>
      </div>
    </main>
  );
}

function subtitle(step: Step, phone: string, otpPurpose: OtpPurpose): string {
  switch (step) {
    case "phone":
      return "Enter your mobile number to continue.";
    case "choose":
      return `Welcome back. How would you like to sign in to ${phone}?`;
    case "acknowledge":
      return "Let’s verify your number.";
    case "password":
      return `Enter the password for ${phone}.`;
    case "otp":
      return otpPurpose === "forgot"
        ? `Enter the code sent to ${phone} to reset your password.`
        : `Enter the 6-digit code sent to ${phone}.`;
    case "set-password":
      return "Create a password to finish setting up your account.";
    case "reset-password":
      return "Choose a new password for your account.";
    case "locked":
      return "This account is temporarily locked.";
  }
}

function Submit({
  busy,
  idle,
  busyLabel,
}: {
  busy: boolean;
  idle: string;
  busyLabel: string;
}) {
  return (
    <button
      type="submit"
      disabled={busy}
      className="btn-primary btn-block"
    >
      {busy ? busyLabel : idle}
    </button>
  );
}

// Refusals of the OTP itself, not of the new password: the reset step sends the user back to the code.
const CODE_ERRORS = ["INVALID_OTP", "OTP_EXPIRED", "OTP_ATTEMPTS_EXCEEDED"];

function ChangeNumber({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-center text-sm text-ink-muted transition hover:text-brand"
    >
      Change number
    </button>
  );
}

function messageFor(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case "RATE_LIMIT_EXCEEDED": {
        // The hourly code cap says when it lifts (AUTH-12).
        const wait = Number(err.details?.retry_after_seconds);
        return wait > 0
          ? `Too many attempts. Please try again in ${Math.ceil(wait / 60)} min.`
          : "Too many attempts. Please try again later.";
      }
      case "INVALID_CREDENTIALS":
        return "Incorrect phone or password.";
      case "ACCOUNT_LOCKED":
        return "Your account is locked after too many attempts. Unlock it with an OTP.";
      case "ACCOUNT_NOT_ACTIVE":
        return "Your account is not active. Please contact an administrator.";
      case "LOGIN_DISABLED":
        return "Sign-in isn't enabled for this account yet. Please contact the administrator.";
      case "INVALID_OTP":
        return "That code is incorrect. Please try again.";
      case "OTP_EXPIRED":
        return "This code has expired. Request a new one.";
      case "OTP_ATTEMPTS_EXCEEDED":
        return "Too many incorrect attempts. Request a new code.";
      // PASSWORD_WEAK: the server's message states the rule it applies (AUTH-15).
      default:
        return err.message;
    }
  }
  return "Something went wrong. Please try again.";
}
